import { CalendarCheck, ChevronLeft, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { ApiError } from '../../../services/api/client'
import { Button } from '../../../ui/Button/Button'
import type {
  PatientRecord,
  ServiceIntent,
} from '../../patients/patients-contracts'
import type { PatientsRepository } from '../../patients/patients-repository'
import { formatPhone } from '../../patients/patient-display'
import { usePatientDialog } from '../../patients/use-patient-dialog'
import { usePatients } from '../../patients/use-patients'
import type { AppointmentBookingRepository } from '../appointment-booking-repository'
import type { BookingOption } from '../appointment-booking-contracts'
import styles from '../AppointmentsPage.module.css'

type Step = 'patient' | 'visit' | 'options' | 'review' | 'success'

function localDate() {
  const value = new Date()
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
}

/**
 * Booking answers that prove nothing was booked: an invalid or unauthorized
 * request, or a 409 refusal (patient, age, time no longer available, expired
 * choice). Anything else (a 202 staff review, a 5xx, a dropped connection, an
 * unreadable answer) may follow a booking the practice system accepted.
 */
function bookingFailure(value: unknown): {
  message: string
  tone: 'danger' | 'warning'
} {
  if (value instanceof ApiError && value.code === 'server') {
    if (value.status === 401 || value.status === 403) {
      return {
        message:
          'You don’t have permission to book appointments for this clinic.',
        tone: 'danger',
      }
    }
    if (value.status === 400 || value.status === 409) {
      return {
        message: `${value.reason ?? 'The appointment couldn’t be booked'}. No appointment was created.`,
        tone: 'danger',
      }
    }
  }
  return {
    message:
      'Flio couldn’t confirm whether the appointment was booked. Check the appointment list or your practice system before booking again.',
    tone: 'warning',
  }
}

function optionLabel(option: BookingOption) {
  const parsed = new Date(option.start)
  return Number.isNaN(parsed.getTime())
    ? option.start
    : new Intl.DateTimeFormat(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: option.timeZone,
      }).format(parsed)
}

type BookAppointmentDialogProps = {
  open: boolean
  tenantId: string
  patientsRepository: PatientsRepository
  serviceIntents: ServiceIntent[]
  repository: AppointmentBookingRepository
  initialPatient: PatientRecord | null
  onClose: () => void
  onBooked: () => void
}

// Mounted only while open, so the dialog takes focus when it opens and
// returns it to the opener when it closes.
export function BookAppointmentDialog(props: BookAppointmentDialogProps) {
  return props.open ? <BookAppointmentDialogContent {...props} /> : null
}

function BookAppointmentDialogContent({
  open,
  tenantId,
  patientsRepository,
  serviceIntents,
  repository,
  initialPatient,
  onClose,
  onBooked,
}: BookAppointmentDialogProps) {
  const [step, setStep] = useState<Step>('patient')
  const [query, setQuery] = useState('')
  const [activeQuery, setActiveQuery] = useState('')
  const [patient, setPatient] = useState<PatientRecord | null>(null)
  const [intent, setIntent] = useState('')
  const [patientType, setPatientType] = useState<'NEW' | 'EXISTING'>('EXISTING')
  const [age, setAge] = useState('')
  const [startDate, setStartDate] = useState(localDate)
  const [options, setOptions] = useState<BookingOption[]>([])
  const [selected, setSelected] = useState<BookingOption | null>(null)
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID())
  const [confirmedAtMs, setConfirmedAtMs] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{
    message: string
    tone: 'danger' | 'warning'
  } | null>(null)
  const dialog = useRef<HTMLElement>(null)
  usePatientDialog(dialog, { onClose, locked: busy })

  useEffect(() => {
    if (!open) return
    setStep(initialPatient ? 'visit' : 'patient')
    setPatient(initialPatient)
    setPatientType(initialPatient?.recordStatus === 'NEW' ? 'NEW' : 'EXISTING')
    setQuery('')
    setActiveQuery('')
    setIntent('')
    setAge('')
    setStartDate(localDate())
    setOptions([])
    setSelected(null)
    setSessionId(crypto.randomUUID())
    setConfirmedAtMs(null)
    setBusy(false)
    setError(null)
  }, [initialPatient, open])

  useEffect(() => {
    if (!open) return
    const timeout = window.setTimeout(() => setActiveQuery(query.trim()), 300)
    return () => window.clearTimeout(timeout)
  }, [open, query])
  const patientResults = usePatients(
    patientsRepository,
    open && step === 'patient' ? tenantId : '',
    { query: activeQuery, prefetchNext: false }
  )
  const matches = patientResults.patients.slice(0, 12)

  if (!open) return null

  // Reading times books nothing, so a failure here is never a booking question.
  function optionsFailure(value: unknown) {
    return {
      message:
        value instanceof ApiError && value.reason
          ? `${value.reason}.`
          : 'Available times couldn’t be loaded. Try again.',
      tone: 'danger' as const,
    }
  }

  async function loadOptions() {
    if (!patient || !intent) return
    setBusy(true)
    setError(null)
    try {
      const response = await repository.options(tenantId, {
        patientAuthorityId: patient.patientAuthorityId,
        serviceIntent: intent,
        patientType,
        startDate,
        patientAgeYears: age === '' ? null : Number(age),
      })
      setOptions(response.options)
      setStep('options')
      if (response.options.length === 0)
        setError({
          message:
            'No open times were found. Try a later start date or another visit type.',
          tone: 'warning',
        })
    } catch (value) {
      setError(optionsFailure(value))
    } finally {
      setBusy(false)
    }
  }

  async function book() {
    if (!patient || !selected || !intent) return
    const confirmationTime = confirmedAtMs ?? Date.now()
    setConfirmedAtMs(confirmationTime)
    setBusy(true)
    setError(null)
    try {
      await repository.book(tenantId, {
        patientAuthorityId: patient.patientAuthorityId,
        serviceIntent: intent,
        patientType,
        slotProofId: selected.slotProofId,
        patientAgeYears: age === '' ? null : Number(age),
        dashboardSessionId: sessionId,
        confirmedAtMs: confirmationTime,
      })
      setStep('success')
      onBooked()
    } catch (value) {
      setError(bookingFailure(value))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.modalBackdrop} role="presentation">
      <section
        ref={dialog}
        className={styles.bookingDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="book-title"
      >
        <header className={styles.dialogHeader}>
          <div>
            <span className={styles.eyebrow}>New appointment</span>
            <h2 id="book-title">Book appointment</h2>
          </div>
          <button
            type="button"
            aria-label="Close booking"
            disabled={busy}
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>
        <div className={styles.stepRail} aria-label="Booking progress">
          {['Patient', 'Visit', 'Availability', 'Review'].map(
            (label, index) => (
              <span
                key={label}
                data-active={
                  index <=
                  ['patient', 'visit', 'options', 'review', 'success'].indexOf(
                    step
                  )
                }
              >
                {label}
              </span>
            )
          )}
        </div>

        {step === 'patient' ? (
          <div className={styles.dialogBody}>
            <h3>Choose a patient</h3>
            <input
              aria-label="Search patients"
              placeholder="Search by name, phone or email"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              data-autofocus
            />
            <div className={styles.patientChoices}>
              {matches.map((item) => (
                <button
                  type="button"
                  key={item.patientAuthorityId}
                  onClick={() => {
                    setPatient(item)
                    setPatientType(
                      item.recordStatus === 'NEW' ? 'NEW' : 'EXISTING'
                    )
                    setStep('visit')
                  }}
                >
                  <strong>{item.displayName}</strong>
                  {item.phone ? <span>{formatPhone(item.phone)}</span> : null}
                </button>
              ))}
              {patientResults.status === 'loading' ? (
                <p role="status">Searching patients…</p>
              ) : null}
              {patientResults.status === 'ready' && matches.length === 0 ? (
                <p>No patients match this search.</p>
              ) : null}
            </div>
          </div>
        ) : null}

        {step === 'visit' && patient ? (
          <div className={styles.dialogBody}>
            <h3>Visit details</h3>
            <div className={styles.selectedPatient}>
              <strong>{patient.displayName}</strong>
              <span>{formatPhone(patient.phone) ?? 'No phone on file'}</span>
            </div>
            <label>
              Visit type
              <select
                value={intent}
                onChange={(event) => setIntent(event.target.value)}
              >
                <option value="">Select a visit type</option>
                {serviceIntents.map((item) => (
                  <option key={item.serviceIntent} value={item.serviceIntent}>
                    {item.serviceLabel}
                  </option>
                ))}
              </select>
            </label>
            <div className={styles.formGrid}>
              <label>
                Patient status
                <select
                  value={patientType}
                  onChange={(event) =>
                    setPatientType(event.target.value as 'NEW' | 'EXISTING')
                  }
                >
                  <option value="EXISTING">Existing patient</option>
                  <option value="NEW">New patient</option>
                </select>
              </label>
              <label>
                Age, when required
                <input
                  inputMode="numeric"
                  min="0"
                  max="130"
                  type="number"
                  value={age}
                  onChange={(event) => setAge(event.target.value)}
                />
              </label>
              <label>
                Search from
                <input
                  type="date"
                  min={localDate()}
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </label>
            </div>
            <div className={styles.dialogActions}>
              <Button
                variant="secondary"
                type="button"
                onClick={() => setStep('patient')}
              >
                Back
              </Button>
              <Button
                type="button"
                disabled={!intent || busy}
                onClick={() => void loadOptions()}
              >
                {busy ? 'Checking…' : 'Check live availability'}
              </Button>
            </div>
          </div>
        ) : null}

        {step === 'options' ? (
          <div className={styles.dialogBody}>
            <h3>Available times</h3>
            <div className={styles.slotChoices}>
              {options.map((option) => (
                <button
                  type="button"
                  key={option.slotProofId}
                  onClick={() => {
                    setSelected(option)
                    setStep('review')
                  }}
                >
                  <strong>{optionLabel(option)}</strong>
                  {option.providerName ? (
                    <span>{option.providerName}</span>
                  ) : null}
                  {option.operatoryName ? (
                    <span>{option.operatoryName}</span>
                  ) : null}
                </button>
              ))}
            </div>
            <div className={styles.dialogActions}>
              <Button
                variant="secondary"
                type="button"
                onClick={() => setStep('visit')}
              >
                Change visit details
              </Button>
            </div>
          </div>
        ) : null}

        {step === 'review' && patient && selected ? (
          <div className={styles.dialogBody}>
            <h3>Review and book</h3>
            <dl className={styles.reviewGrid}>
              <div>
                <dt>Patient</dt>
                <dd>{patient.displayName}</dd>
              </div>
              <div>
                <dt>Visit</dt>
                <dd>
                  {serviceIntents.find((item) => item.serviceIntent === intent)
                    ?.serviceLabel ?? intent}
                </dd>
              </div>
              <div>
                <dt>Time</dt>
                <dd>{optionLabel(selected)}</dd>
              </div>
              {selected.providerName ? (
                <div>
                  <dt>Provider</dt>
                  <dd>{selected.providerName}</dd>
                </div>
              ) : null}
              {selected.operatoryName ? (
                <div>
                  <dt>Room</dt>
                  <dd>{selected.operatoryName}</dd>
                </div>
              ) : null}
            </dl>
            <p className={styles.confirmNotice}>
              Flio checks this time is still open, then books it once in your
              practice system.
            </p>
            <div className={styles.dialogActions}>
              <Button
                variant="secondary"
                type="button"
                disabled={busy}
                onClick={() => setStep('options')}
              >
                <ChevronLeft size={16} aria-hidden="true" /> Back
              </Button>
              <Button type="button" disabled={busy} onClick={() => void book()}>
                {busy ? 'Booking…' : 'Confirm and book'}
              </Button>
            </div>
          </div>
        ) : null}

        {step === 'success' ? (
          <div className={styles.successPanel}>
            <CalendarCheck size={32} aria-hidden="true" />
            <h3>Appointment confirmed</h3>
            <p>
              It’s booked and appears in the appointment list. Your practice
              system may take a few minutes to show it.
            </p>
            <Button type="button" onClick={onClose}>
              Done
            </Button>
          </div>
        ) : null}
        {error ? (
          <p className={styles.dialogError} data-tone={error.tone} role="alert">
            {error.message}
          </p>
        ) : null}
      </section>
    </div>
  )
}
